// Loan cache = latest server truth (by revision) ∘ pending optimistic edits.
//
// Every server copy of a loan (query fetch, SSE event, PATCH/batch/undo response) goes through
// `acceptServerLoan`, which applies it only if its revision ≥ the one we hold. The values shown in
// the React Query caches are the server copy overlaid with any edits still in flight, so a live
// event for our own earlier save never flickers away a newer edit that is still queued.
import type { QueryClient } from "@tanstack/react-query";
import type { EditableField, FieldValue, Loan, LoanSet, LoansResponse, Me } from "./types";
import { revenue } from "./blend";
import { revPct } from "./domain";

const server = new Map<string, Loan>();
const removed = new Map<string, number>();
let epoch = 0;
export const cacheEpoch = () => epoch;
export function resetLoanCache() { server.clear(); pending.clear(); removed.clear(); epoch++; }

const pending = new Map<string, Map<EditableField, { token: number; value: FieldValue }[]>>();
let seq = 0;

export function serverLoan(id: string): Loan | undefined {
  return server.get(id);
}

/** Returns true when the copy was accepted (newer or same revision). */
export function recordServer(loan: Loan): boolean {
  if ((removed.get(loan.id) ?? -1) >= loan.revision) return false;
  const cur = server.get(loan.id);
  if (cur && cur.revision > loan.revision) return false;
  server.set(loan.id, loan);
  return true;
}

export function forgetServer(id: string) {
  server.delete(id);
  pending.delete(id);
}

export function addPending(id: string, set: LoanSet): number {
  const token = ++seq;
  let m = pending.get(id);
  if (!m) pending.set(id, (m = new Map()));
  for (const [f, v] of Object.entries(set) as [EditableField, FieldValue][]) {
    const stack = m.get(f) ?? [];
    stack.push({ token, value: v });
    m.set(f, stack);
  }
  return token;
}

export function clearPending(id: string, token: number) {
  const m = pending.get(id);
  if (!m) return;
  for (const [f, stack] of m) {
    const next = stack.filter((e) => e.token !== token);
    if (next.length) m.set(f, next);
    else m.delete(f);
  }
  if (!m.size) pending.delete(id);
}

export function hasPending(id: string): boolean {
  return (pending.get(id)?.size ?? 0) > 0;
}

/** Recompute the derived money fields locally (server recomputes authoritatively). */
export function withDerived(l: Loan): Loan {
  const rev = revenue(l.amount, l.price);

  return {
    ...l,
    revenue_amount: rev === null ? null : rev.toFixed(2),
    rev_pct: revPct(l.price),
  };
}

export function overlay(base: Loan): Loan {
  const m = pending.get(base.id);
  if (!m || !m.size) return base;
  const next: Record<string, unknown> = { ...base };
  for (const [f, stack] of m) next[f] = stack[stack.length - 1].value;
  const out = next as unknown as Loan;
  return m.has("amount") || m.has("price") ? withDerived(out) : out;
}

type LoansKey = readonly ["loans", string, string | null, string?];

function belongs(loan: Loan, key: LoansKey, me: Me): boolean {
  const [, scope, teamId, lo] = key;
  // One LO's loans: every viewable loan linked to that LO, whatever the scope.
  if (lo) return loan.lo_person_id === lo;
  if (scope === "all") return teamId ? loan.team_id === teamId : true;
  // The server sends only loans the user is assigned to; whatever it sends belongs.
  if (scope === "assigned") return true;
  if (scope === "mine") {
    return me.user.role === "rm_support" ? me.user.supported_ae_ids.includes(loan.owner_user_id) : loan.owner_user_id === me.user.id;
  }
  if (teamId) return loan.team_id === teamId;
  const defaultTeam = me.teams.find((t) => t.id === me.user.team_id)?.id ?? me.teams[0]?.id;
  return loan.team_id === defaultTeam;
}

function eachLoansQuery(qc: QueryClient, fn: (key: LoansKey, data: LoansResponse) => LoansResponse | undefined) {
  for (const q of qc.getQueryCache().findAll({ queryKey: ["loans"] })) {
    const data = q.state.data as LoansResponse | undefined;
    if (!data) continue;
    const next = fn(q.queryKey as unknown as LoansKey, data);
    if (next && next !== data) qc.setQueryData(q.queryKey, next);
  }
}

/** Re-render one loan in every loans cache from server ∘ pending. Inserts when it belongs to the scope. */
export function refreshLoan(qc: QueryClient, id: string, me: Me | undefined, allowInsert: boolean) {
  const base = server.get(id);
  if (!base) return;
  const shown = overlay(base);
  eachLoansQuery(qc, (key, data) => {
    const idx = data.loans.findIndex((l) => l.id === id);
    if (idx >= 0 && me && !belongs(shown, key, me)) {
      return { ...data, loans: data.loans.filter((l) => l.id !== id) };
    }
    if (idx >= 0) {
      const loans = data.loans.slice();
      loans[idx] = shown;
      return { ...data, loans };
    }
    if (allowInsert && me && belongs(shown, key, me)) return { ...data, loans: [...data.loans, shown] };
    return undefined;
  });
}

export function acceptServerLoan(qc: QueryClient, loan: Loan, me: Me | undefined, allowInsert = true) {
  if (!recordServer(loan)) return;
  refreshLoan(qc, loan.id, me, allowInsert);
}

export function removeLoan(qc: QueryClient, id: string, revision?: number) {
  const rev = revision ?? server.get(id)?.revision ?? 0;
  if ((server.get(id)?.revision ?? -1) > rev) return;
  removed.set(id, Math.max(removed.get(id) ?? -1, rev));
  forgetServer(id);
  eachLoansQuery(qc, (_key, data) =>
    data.loans.some((l) => l.id === id) ? { ...data, loans: data.loans.filter((l) => l.id !== id) } : undefined,
  );
}

/** Used by the loans queryFn: record the fetched copies and return them overlaid. */
export function ingestFetched(res: LoansResponse): LoansResponse {
  return {
    ...res,
    loans: res.loans.filter((l) => (removed.get(l.id) ?? -1) < l.revision).map((l) => {
      recordServer(l);
      return overlay(server.get(l.id) ?? l);
    }),
  };
}

export function findCachedLoan(qc: QueryClient, id: string): Loan | undefined {
  for (const q of qc.getQueryCache().findAll({ queryKey: ["loans"] })) {
    const l = (q.state.data as LoansResponse | undefined)?.loans.find((x) => x.id === id);
    if (l) return l;
  }
  // Server copy only (stable identity for useSyncExternalStore snapshots).
  return server.get(id);
}
